import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Typography, Button, Box } from "@mui/material";
import Modal from "../../../../components/Modal/Modal";
import * as Yup from "yup";
import { Form, Field, Formik } from "formik";
import { TextField } from "formik-mui";
import RadioGroup from "../../../../components/FormikFields/RadioGroup";
import DatePickerField from "../../../../components/FormikFields/DatePickerField";
import {
  formatDate,
  isValidDate,
  isBeforeDate
} from "../../../../utils/datetime.js";
import moment from "moment";

const getCourseExclusionSettingsSchema = t =>
  Yup.object().shape({
    comment: Yup.string()
      .max(
        150,
        t("validationMessages.cannotExceedLength", {
          type: t("courseVersion.details.title"),
          maxLength: 150
        })
      )
      .required(t("validationMessages.fieldRequired")),
    expiresAt: Yup.mixed().when("expires", {
      is: "true",
      then: Yup.mixed()
        .required(t("validationMessages.fieldRequired"))
        .test("expiresAt", t("validationMessages.validDate"), function() {
          return isValidDate(this.parent.expiresAt);
        })
        .test(
          "minDate",
          t("validationMessages.dateCannotBeInPast"),
          function() {
            return isBeforeDate(new Date(), this.parent.expiresAt);
          }
        )
    })
  });

const CourseExclusionSettingsModalForm = ({
  submitForm,
  isSubmitting,
  values,
  resetForm,
  open,
  onClose,
  title,
  setFieldValue
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    resetForm();
  }, [open]);

  useEffect(() => {
    if (values.expires === "false") {
      setFieldValue("expiresAt", null);
    } else if (!values.expiresAt) {
      setFieldValue("expiresAt", moment());
    }
  }, [values.expires]);

  return (
    <Form data-qa="course-exclusion-settings-modal-form">
      <Modal
        maxWidth="sm"
        fullWidth
        dialogTitle={
          <Typography variant="h4" color="textPrimary">
            {title}
          </Typography>
        }
        dialogContent={
          <Box display="flex" flexDirection="column" mb={1}>
            <Box mb={3}>
              <RadioGroup
                radioButtons={[
                  { label: t("yes"), value: "true" },
                  { label: t("no"), value: "false" }
                ]}
                name="expires"
                label={t("courseExclusions.settingsModal.expires")}
                disabled={isSubmitting}
                data-qa="course-exclusion-settings-modal-expires"
              />
            </Box>
            {values.expires === "true" && (
              <Box mb={3}>
                <Field
                  minDate={new Date()}
                  name="expiresAt"
                  component={DatePickerField}
                  label={t("courseExclusions.settingsModal.expiresAt")}
                  disabled={isSubmitting}
                />
              </Box>
            )}
            <Box mb={3}>
              <Field
                variant="filled"
                fullWidth
                label={t("courseExclusions.settingsModal.comment")}
                component={TextField}
                name="comment"
                disabled={isSubmitting}
                inputProps={{
                  "data-qa": "course-exclusion-settings-modal-comment"
                }}
              />
            </Box>
          </Box>
        }
        dialogActions={
          <React.Fragment>
            <Button
              variant="contained"
              color="primary"
              onClick={submitForm}
              disabled={isSubmitting}
            >
              {t("saveBtn")}
            </Button>
            <Button color="primary" disabled={isSubmitting} onClick={onClose}>
              {t("cancelBtn")}
            </Button>
          </React.Fragment>
        }
        open={open}
        onClose={onClose}
      />
    </Form>
  );
};

const CourseExclusionSettingsModal = ({
  onClose,
  open,
  onSubmit,
  title,
  initialValues
}) => {
  const user = useSelector(state => state.userState.user);
  const { t } = useTranslation();
  return (
    <Formik
      initialValues={{
        expires: initialValues ? `${!!initialValues.expiresAt}` : "false",
        expiresAt:
          initialValues && !!initialValues.expiresAt
            ? initialValues.expiresAt
            : null,
        comment:
          initialValues && initialValues.comment
            ? initialValues.comment
            : `${t("userAssignments.excludedBy")} ${
                user ? `${user.firstName} ${user.lastName}` : ""
              } (${formatDate(new Date())})`
      }}
      validationSchema={getCourseExclusionSettingsSchema(t)}
      onSubmit={(values, { setSubmitting }) =>
        onSubmit(
          values,
          () => setSubmitting(false),
          error => {
            setSubmitting(false);
            console.log(error);
            alert(error);
          }
        )
      }
      enableReinitialize
    >
      {({
        submitForm,
        isSubmitting,
        values,
        resetForm,
        setFieldValue,
        errors
      }) => (
        <CourseExclusionSettingsModalForm
          submitForm={submitForm}
          isSubmitting={isSubmitting}
          values={values}
          resetForm={resetForm}
          open={open}
          onClose={onClose}
          setFieldValue={setFieldValue}
          errors={errors}
          title={title}
        />
      )}
    </Formik>
  );
};

CourseExclusionSettingsModal.propTypes = {
  /**
   * Called to close the Add Course Version Modal
   */
  onClose: PropTypes.func,
  /**
   * True if the modal is open, false otherwise
   */
  open: PropTypes.bool,
  /**
   * Called when the form is submitted to create the new version
   */
  onSubmit: PropTypes.func
};

export default CourseExclusionSettingsModal;
